/**
 * Article reading experience — scoped to pages containing [data-article-body]
 * (i.e. News and views articles).
 *
 * Everything here is progressive enhancement. The server ships the reading bar
 * and the contents card hidden and empty; if this file never runs, the article
 * reads exactly as it did before.
 *
 * Provides:
 * - A scroll-progress line across the top of the sticky reading bar
 * - A reading bar that slides in once the reader reaches the body, showing the
 *   article title, time remaining, a contents menu and share actions
 * - Auto-generated jump links, built from the rendered <h2>/<h3> headings, so
 *   they work whether a heading came from a `heading` block or from inside a
 *   CKEditor `text` block
 * - Scrollspy highlighting of the section currently in view
 * - Copy-link and native-share buttons where the browser supports them
 */
(function () {
	const body = document.querySelector('[data-article-body]');
	if (!body) return;

	// Below this, a contents menu is more clutter than help.
	const CONTENTS_MIN_HEADINGS = 3;
	// Reveal the bar once this much of the article has scrolled past the top.
	const REVEAL_AFTER_PX = 120;

	const readbar = document.querySelector('[data-article-readbar]');
	const progressFill = document.querySelector('[data-article-progress]');
	const remaining = document.querySelector('[data-article-remaining]');
	const contentsMenu = document.querySelector('[data-article-contents-menu]');
	const contentsToggle = document.querySelector('[data-article-contents-toggle]');
	const contentsPanel = document.getElementById('article-contents-menu');
	const contentsCard = document.querySelector('[data-article-contents-card]');
	const contentsLists = Array.prototype.slice.call(
		document.querySelectorAll('[data-article-contents-list]')
	);

	const totalMinutes = readbar ? parseInt(readbar.getAttribute('data-article-minutes'), 10) || 0 : 0;
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	// ── Headings → jump links ───────────────────────────────────────────────
	// Headings inside the body can arrive from either a `heading` matrix block
	// or the rich text of a `text` block, so read them off the DOM rather than
	// trying to enumerate blocks server-side.

	function slugify(text) {
		return text
			.toLowerCase()
			.replace(/[‘’“”']/g, '')
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/^-+|-+$/g, '')
			.slice(0, 60);
	}

	function collectHeadings() {
		const nodes = Array.prototype.slice.call(body.querySelectorAll('h2, h3'));
		const used = {};

		return nodes
			.filter(function (node) {
				// Never let the contents list index itself, and ignore headings
				// an editor has left empty.
				if (node.closest('[data-article-contents-card]')) return false;
				return node.textContent.trim().length > 0;
			})
			.map(function (node) {
				let id = node.id;
				if (!id) {
					id = slugify(node.textContent) || 'section';
					// Two headings can share wording; keep the anchors unique.
					if (used[id]) {
						used[id] += 1;
						id = id + '-' + used[id];
					} else {
						used[id] = 1;
					}
					node.id = id;
				}
				return {
					id: id,
					text: node.textContent.trim(),
					level: node.tagName === 'H3' ? 3 : 2,
					node: node,
				};
			});
	}

	const headings = collectHeadings();
	const links = [];

	if (headings.length >= CONTENTS_MIN_HEADINGS) {
		contentsLists.forEach(function (list) {
			const fragment = document.createDocumentFragment();

			headings.forEach(function (heading) {
				const item = document.createElement('li');
				item.className = 'article-contents__item article-contents__item--level-' + heading.level;

				const link = document.createElement('a');
				link.href = '#' + heading.id;
				link.textContent = heading.text;
				link.className = 'article-contents__link';
				link.setAttribute('data-article-contents-link', heading.id);

				item.appendChild(link);
				fragment.appendChild(item);
				links.push(link);
			});

			list.appendChild(fragment);
		});

		if (contentsCard) {
			contentsCard.hidden = false;
			// Open on wide screens, collapsed where vertical space is tight.
			contentsCard.open = window.matchMedia('(min-width: 1024px)').matches;
		}
		if (contentsMenu) contentsMenu.hidden = false;
	}

	// Offset jump targets so a heading doesn't land underneath the reading bar.
	// A hidden element measures 0, so remember the real height once we have it
	// and start from the CSS height — jumping from the contents card at the top
	// of the article happens before the bar has ever been shown, and the target
	// still has to clear it once it slides in.
	let readbarHeight = 56;

	function scrollOffset() {
		if (!readbar) return 16;
		if (!readbar.hidden) readbarHeight = readbar.offsetHeight;
		return readbarHeight + 16;
	}

	function jumpTo(id) {
		const target = document.getElementById(id);
		if (!target) return;
		const top = window.pageYOffset + target.getBoundingClientRect().top - scrollOffset();
		window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
	}

	links.forEach(function (link) {
		link.addEventListener('click', function (event) {
			event.preventDefault();
			const id = link.getAttribute('data-article-contents-link');
			jumpTo(id);
			closeContents();
			// Keep the URL shareable and the history sane.
			if (window.history && window.history.replaceState) {
				window.history.replaceState(null, '', '#' + id);
			}
		});
	});

	// ── Contents dropdown in the reading bar ────────────────────────────────

	function openContents() {
		if (!contentsToggle || !contentsPanel) return;
		contentsPanel.hidden = false;
		contentsToggle.setAttribute('aria-expanded', 'true');
	}

	function closeContents() {
		if (!contentsToggle || !contentsPanel) return;
		contentsPanel.hidden = true;
		contentsToggle.setAttribute('aria-expanded', 'false');
	}

	if (contentsToggle) {
		contentsToggle.addEventListener('click', function () {
			if (contentsToggle.getAttribute('aria-expanded') === 'true') {
				closeContents();
			} else {
				openContents();
			}
		});

		document.addEventListener('click', function (event) {
			if (!contentsMenu || contentsMenu.contains(event.target)) return;
			closeContents();
		});

		document.addEventListener('keydown', function (event) {
			if (event.key !== 'Escape') return;
			closeContents();
		});
	}

	// ── Scrollspy ───────────────────────────────────────────────────────────
	// Measured from the scroll handler rather than an IntersectionObserver: the
	// section the reader is in is simply the last heading to have reached the
	// reading bar, and asking the rects directly gets that boundary exactly
	// right — including straight after a jump link, where the target heading
	// lands flush against the bar.

	let activeId = null;

	function syncActiveHeading() {
		if (!links.length) return;

		const limit = scrollOffset() + 4;
		let current = headings[0].id;
		for (let i = 0; i < headings.length; i++) {
			if (headings[i].node.getBoundingClientRect().top > limit) break;
			current = headings[i].id;
		}

		if (current === activeId) return;
		activeId = current;

		links.forEach(function (link) {
			const isActive = link.getAttribute('data-article-contents-link') === current;
			link.classList.toggle('is-active', isActive);
			if (isActive) {
				link.setAttribute('aria-current', 'true');
			} else {
				link.removeAttribute('aria-current');
			}
		});
	}

	// ── Progress, reveal and time remaining ─────────────────────────────────

	let ticking = false;

	function update() {
		ticking = false;

		const rect = body.getBoundingClientRect();
		const scrolled = -rect.top;
		// Full when the end of the article reaches the bottom of the viewport —
		// not the end of the *page*, which also carries the author bio and the
		// read-next rail.
		const travel = Math.max(1, rect.height - window.innerHeight);
		const progress = Math.min(1, Math.max(0, scrolled / travel));

		if (progressFill) {
			progressFill.style.transform = 'scaleX(' + progress + ')';
		}

		if (readbar) {
			const shouldShow = scrolled > REVEAL_AFTER_PX;
			if (shouldShow && readbar.hidden) {
				readbar.hidden = false;
				// Let the browser register the un-hidden element before animating.
				window.requestAnimationFrame(function () {
					readbar.classList.add('is-visible');
				});
			} else if (!shouldShow && !readbar.hidden) {
				readbar.classList.remove('is-visible');
				readbar.hidden = true;
				closeContents();
			}
		}

		if (remaining && totalMinutes) {
			const label = remaining.querySelector('span');
			if (label) {
				if (progress >= 0.98) {
					label.textContent = 'Finished';
				} else if (progress < 0.03) {
					label.textContent = totalMinutes + ' min read';
				} else {
					const left = Math.max(1, Math.ceil(totalMinutes * (1 - progress)));
					label.textContent = left + ' min left';
				}
			}
		}

		syncActiveHeading();
	}

	function onScroll() {
		if (ticking) return;
		ticking = true;
		window.requestAnimationFrame(update);
	}

	window.addEventListener('scroll', onScroll, { passive: true });
	window.addEventListener('resize', onScroll, { passive: true });
	update();

	// ── Back to top ─────────────────────────────────────────────────────────

	const toTop = document.querySelector('[data-article-to-top]');
	if (toTop) {
		toTop.addEventListener('click', function () {
			window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
		});
	}

	// ── Copy link ───────────────────────────────────────────────────────────

	if (navigator.clipboard && window.isSecureContext) {
		document.querySelectorAll('[data-article-copy-link]').forEach(function (button) {
			button.classList.remove('hidden');
			const icon = button.querySelector('i');
			const originalClass = icon ? icon.className : null;

			button.addEventListener('click', function () {
				const url = button.getAttribute('data-copy-url');
				navigator.clipboard.writeText(url).then(function () {
					button.classList.add('is-copied');
					if (icon) icon.className = originalClass.replace('fa-link', 'fa-check');
					window.setTimeout(function () {
						button.classList.remove('is-copied');
						if (icon && originalClass) icon.className = originalClass;
					}, 2000);
				});
			});
		});
	}

	// ── Native share sheet (mostly mobile) ──────────────────────────────────

	if (navigator.share) {
		document.querySelectorAll('[data-article-native-share]').forEach(function (button) {
			button.classList.remove('hidden');
			button.addEventListener('click', function () {
				navigator
					.share({
						title: button.getAttribute('data-share-title'),
						url: button.getAttribute('data-share-url'),
					})
					// A dismissed share sheet rejects; that isn't an error.
					.catch(function () {});
			});
		});
	}
})();
